import { ModalStack, useModals } from 'almanac-ui/Modal'
import { useEffect, useState } from 'react'
import { observer } from 'mobx-react-lite'
import { useRootStore } from 'stores/RootStore'
import { useEnforceBlazeFreeTrial } from 'hooks/useEnforceBlazeFreeTrial'
import { useCurrentWorkspace } from 'hooks/useCurrentWorkspace'
import { useOnboarding } from 'components/Onboarding/useOnboarding'
import { useLoggedIn } from 'hooks/useLoggedIn'
import { Workspace } from 'types'
import { useFlipperFlag } from 'hooks/useFlipperFlag'
import { CSSTransition } from 'react-transition-group'

import { AspectRatioKey } from 'components/NewFileModal/Types'
import { useCreateDesign } from 'screens/blaze/Home/components/Catalogue/components/useCreateDesign'
import { useQueryParams } from 'hooks/useQueryParams'
import { useOnboard } from 'hooks/queries/useOnboard'
import { RegistrationAndOnboarding } from 'screens/Registration/RegistrationAndOnboarding'
import { useLocation } from 'react-router-dom'
import { REGISTRATION_PATH, SELECT_WORKSPACE_PATH } from 'paths'
import { OnboardingAndBrandKit } from 'screens/OnboardingAndBrandKitForm/OnboardingAndBrandKitForm'
import { useAuth } from 'hooks/UseAuth'
import { DarkModeExtensionWarning } from './DarkModeExtensionWarning'
import { useDarkReaderLock } from 'hooks/useDarkReaderLock'
import styles from './Onboarding.module.scss'

const FullscreenOnboarding = ({ isVisible }: { isVisible: boolean }) => {
  // Ensure dark mode extension lock meta tag exists (fallback if not in HTML)
  useDarkReaderLock()

  return (
    <>
      <DarkModeExtensionWarning />
      <CSSTransition
        in={isVisible}
        timeout={300}
        appear
        unmountOnExit
        classNames={{
          appear: styles.enter,
          appearActive: styles.enterActive,
          enter: styles.enter,
          enterActive: styles.enterActive,
          exit: styles.exit,
          exitActive: styles.exitActive,
        }}
      >
        <div className={styles.mounter}>
          <div className={styles.presenter}>
            <OnboardingAndBrandKit />
          </div>
        </div>
      </CSSTransition>
    </>
  )
}

interface OnboardingProps {
  onGenerateContent: ({ templateId, aspectRatioKey }: { templateId: number; aspectRatioKey: AspectRatioKey }) => void
}

const Onboarding = ({ onGenerateContent }: OnboardingProps) => {
  const { openModal } = useModals()
  const { templateId, aspectRatioKey } = useQueryParams()
  const { mutateAsync: onboard } = useOnboard()
  const { featureFlags } = useRootStore()
  const isNewRegistration = useFlipperFlag('new_registration')
  const { pathname } = useLocation()
  const isOnRegistration = pathname.includes(REGISTRATION_PATH)
  const isOnAutopilotWorkspaceSelection = pathname.includes(SELECT_WORKSPACE_PATH)
  const [showFullscreenOnboarding, setShowFullscreenOnboarding] = useState(false)

  useEffect(() => {
    if (!featureFlags.ready || isOnRegistration || isOnAutopilotWorkspaceSelection) return

    const timeout = setTimeout(async () => {
      if (templateId && aspectRatioKey) {
        await onboard()
        onGenerateContent({ templateId, aspectRatioKey })
      } else if (isNewRegistration) {
        openModal(RegistrationAndOnboarding)
      } else {
        setShowFullscreenOnboarding(true)
      }
    }, 1000)
    return () => clearTimeout(timeout)
  }, [featureFlags.ready]) // eslint-disable-line react-hooks/exhaustive-deps

  return <FullscreenOnboarding isVisible={showFullscreenOnboarding} />
}

const OnboardingContainer = observer(() => {
  const currentWorkspace = useCurrentWorkspace()
  const isLoggedIn = useLoggedIn()

  if (!currentWorkspace || !isLoggedIn || currentWorkspace.is_virtual_marketer) return null

  return <WithWorkspace workspace={currentWorkspace} />
})

const WithWorkspace = observer(({ workspace }: { workspace: Workspace }) => {
  const { onboardingStore } = useRootStore()
  const { showPaywall } = useEnforceBlazeFreeTrial()
  const { user, member } = useOnboarding()
  const auth = useAuth()

  const { createDesign: openDesignWizard } = useCreateDesign()

  const handleGenerateContent = ({
    templateId,
    aspectRatioKey,
  }: {
    templateId: number
    aspectRatioKey: AspectRatioKey
  }) => {
    openDesignWizard({
      aspectRatioKey,
      initialValues: { source_visual_element_id: templateId, startWithTemplate: true },
    })
  }

  useEffect(() => {
    onboardingStore.hydrate(workspace.key)
  }, [workspace.key, onboardingStore])

  if (showPaywall || !member || !user) return null

  if (auth?.onboarded || user.onboarded || member.company_role === 'guest') return null

  return (
    <ModalStack>
      <Onboarding onGenerateContent={handleGenerateContent} />
    </ModalStack>
  )
})

export { OnboardingContainer as Onboarding }
